import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2, Trophy } from 'lucide-react';
import {
  CHALLENGE_KINDS,
  CHALLENGE_METRICS,
  type ChallengeDto,
  type UpdateChallengeInput,
} from '@gc/shared';

import { api } from '@/lib/api';
import {
  Field,
  ImageUpload,
  Toggle,
  ghostPillClass,
  inputClass,
  pillClass,
} from '@/features/settings/controls';
import { toFieldErrors, useResource, type FieldError } from '@/features/settings/useSettings';
import {
  CHALLENGES_PATH,
  CHALLENGE_GAMES_PATH,
  DEFAULT_CHALLENGE_GAME_FILTER,
  challengeFormFromDto,
  challengeGameFilters,
  challengeMissingMetric,
  challengeMissingTarget,
  effectiveChallengeGameFilter,
  filterChallengesByGame,
  gamePickerOptions,
  metricKey,
  toChallengePayload,
  toChallengeUpdatePayload,
  type ChallengeFormState,
  type ChallengeGameFilter,
  type ChallengeGameOption,
} from './engagement';

/**
 * Challenges & Quests (SPEC §9) — the catalogue customers see in the portal's
 * Arcade, with the operational columns (active flag, completion count) the
 * portal never shows.
 */
export function ChallengesPage() {
  const { t, i18n } = useTranslation();

  const { data, loading, error: loadError, reload } = useResource<ChallengeDto[]>(CHALLENGES_PATH);
  const challenges = data ?? [];

  // By game (owner, 2026-09-23): the filter only exists once some challenge
  // names a game, and only offers the games that some challenge does.
  const [gameFilter, setGameFilter] = useState<ChallengeGameFilter>(DEFAULT_CHALLENGE_GAME_FILTER);
  const gameFilters = challengeGameFilters(challenges);
  const activeGameFilter = effectiveChallengeGameFilter(challenges, gameFilter);
  const shown = filterChallengesByGame(challenges, activeGameFilter);
  const gameLabel = (challenge: ChallengeDto) =>
    challenge.gameTitle ?? t('portalContent.challenges.allGames');

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ChallengeDto | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * A refused DELETE (the challenge has completions) keeps the server's
   * explanation on screen next to an escape hatch: deactivating hides the
   * challenge from customers while preserving the completion history.
   */
  const [deleteRefused, setDeleteRefused] = useState<{
    challenge: ChallengeDto;
    message: string;
  } | null>(null);

  const dateTime = (iso: string | null) => {
    if (!iso) return '—';
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return '—';
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'short',
      timeStyle: 'short',
      numberingSystem: 'latn',
    }).format(parsed);
  };

  async function handleToggle(challenge: ChallengeDto, isActive: boolean): Promise<void> {
    setTogglingId(challenge.id);
    setError(null);
    try {
      await api.patch(`${CHALLENGES_PATH}/${challenge.id}`, {
        isActive,
      } satisfies UpdateChallengeInput);
      await reload();
    } catch (caught) {
      setError(toFieldErrors(caught, t('portalContent.challenges.saveFailed'))[0]?.message ?? null);
    } finally {
      setTogglingId(null);
    }
  }

  async function handleDelete(challenge: ChallengeDto): Promise<void> {
    if (!window.confirm(t('portalContent.challenges.confirmDelete', { title: challenge.title }))) {
      return;
    }
    setError(null);
    try {
      // The API keeps a challenge that has completions: DELETE then answers 200
      // with the row deactivated instead of removed (completionCount > 0 is the
      // server's own keep condition). Say so, or staff watch the row survive a
      // delete with no explanation.
      const outcome = await api.delete<ChallengeDto>(`${CHALLENGES_PATH}/${challenge.id}`);
      if (outcome && outcome.completionCount > 0 && !outcome.isActive) {
        setDeleteRefused({
          challenge: outcome,
          message: t('portalContent.challenges.deleteKeptDeactivated', { title: outcome.title }),
        });
      } else {
        setDeleteRefused(null);
      }
      await reload();
    } catch (caught) {
      const message =
        toFieldErrors(caught, t('portalContent.challenges.deleteFailed'))[0]?.message ??
        t('portalContent.challenges.deleteFailed');
      setDeleteRefused({ challenge, message });
    }
  }

  async function handleDeactivate(challenge: ChallengeDto): Promise<void> {
    try {
      await api.patch(`${CHALLENGES_PATH}/${challenge.id}`, {
        isActive: false,
      } satisfies UpdateChallengeInput);
      setDeleteRefused(null);
      await reload();
    } catch (caught) {
      const message =
        toFieldErrors(caught, t('portalContent.challenges.deactivateFailed'))[0]?.message ??
        t('portalContent.challenges.deactivateFailed');
      setDeleteRefused({ challenge, message });
    }
  }

  return (
    <div className="p-6">
      <header className="mb-4 flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-content">
          {t('portalContent.challenges.title')}
        </h2>

        {gameFilters.length > 0 ? (
          <ChallengeGameFilterSelect
            value={activeGameFilter}
            options={gameFilters}
            onChange={setGameFilter}
          />
        ) : null}

        <div className="flex-1" />

        <button type="button" onClick={() => setCreating(true)} className={pillClass}>
          <span className="inline-flex items-center gap-1">
            <Plus className="h-4 w-4" aria-hidden />
            {t('portalContent.challenges.addNew')}
          </span>
        </button>
      </header>

      {error ? (
        <p role="alert" className="mb-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {deleteRefused ? (
        <div
          role="alert"
          className="mb-3 flex flex-wrap items-center gap-3 rounded-card border border-subtle bg-raised p-3"
        >
          <p className="text-sm text-danger">{deleteRefused.message}</p>
          <div className="flex-1" />
          {deleteRefused.challenge.isActive ? (
            <button
              type="button"
              onClick={() => void handleDeactivate(deleteRefused.challenge)}
              className={ghostPillClass}
            >
              {t('portalContent.deactivateInstead')}
            </button>
          ) : null}
          <button type="button" onClick={() => setDeleteRefused(null)} className={ghostPillClass}>
            {t('portalContent.dismiss')}
          </button>
        </div>
      ) : null}

      {loading && challenges.length === 0 ? (
        <p className="text-sm text-content-muted">{t('common.loading')}</p>
      ) : loadError ? (
        // Before the empty state: a failed GET must never read as "no challenges".
        <p className="text-sm text-danger">{loadError}</p>
      ) : challenges.length === 0 ? (
        <p className="text-sm text-content-muted">{t('portalContent.challenges.empty')}</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-content-muted">
          {t('portalContent.challenges.gameFilter.empty')}
        </p>
      ) : (
        <>
        {/* Phone: a card per challenge — picture, kind, game, title, target,
            reward, dates, completions, the active toggle and both actions. */}
        <div className="space-y-2 md:hidden">
          {shown.map((challenge) => (
            <div key={challenge.id} className="rounded-card border border-subtle bg-raised p-3">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-control bg-input">
                  {challenge.imageUrl ? (
                    <img src={challenge.imageUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <Trophy className="h-4 w-4 text-content-muted" aria-hidden />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-content">{challenge.title}</p>
                  <p className="truncate text-xs text-content-muted">
                    {t(`portalContent.kinds.${challenge.kind}`)}
                    {' · '}
                    {gameLabel(challenge)}
                    {' · '}
                    {challenge.metric && challenge.targetValue !== null ? (
                      <>
                        {challenge.targetValue} {t(metricKey(challenge.metric))}
                      </>
                    ) : (
                      t('portalContent.challenges.oneShot')
                    )}
                    {' · '}
                    {challenge.pointsReward} {t('portalContent.pointsShort')}
                  </p>
                  <p className="tabular truncate text-xs text-content-muted">
                    {dateTime(challenge.startsAt)} – {dateTime(challenge.endsAt)}
                    {' · '}
                    {t('portalContent.challenges.columns.completions')}: {challenge.completionCount}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <input
                    type="checkbox"
                    checked={challenge.isActive}
                    disabled={togglingId === challenge.id}
                    aria-label={t('portalContent.challenges.toggleActive', {
                      title: challenge.title,
                    })}
                    onChange={(event) => void handleToggle(challenge, event.target.checked)}
                    className="h-4 w-4 accent-[var(--brand-primary)]"
                  />
                  <button
                    type="button"
                    onClick={() => setEditing(challenge)}
                    title={t('common.edit')}
                    aria-label={t('common.edit')}
                    className="grid h-10 w-10 place-items-center rounded-control text-content-secondary hover:bg-hover hover:text-content"
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete(challenge)}
                    title={t('common.delete')}
                    aria-label={t('common.delete')}
                    className="grid h-10 w-10 place-items-center rounded-control text-danger hover:bg-hover"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="overflow-x-auto max-md:hidden">
          <table className="w-full min-w-max border-collapse text-sm">
            <thead>
              <tr className="border-b border-subtle">
                <Th>
                  <span className="sr-only">{t('portalContent.challenges.columns.image')}</span>
                </Th>
                <Th>{t('portalContent.challenges.columns.kind')}</Th>
                <Th>{t('portalContent.challenges.columns.title')}</Th>
                <Th>{t('portalContent.challenges.columns.game')}</Th>
                <Th>{t('portalContent.challenges.columns.target')}</Th>
                <Th>{t('portalContent.challenges.columns.reward')}</Th>
                <Th>{t('portalContent.challenges.columns.active')}</Th>
                <Th>{t('portalContent.challenges.columns.startsAt')}</Th>
                <Th>{t('portalContent.challenges.columns.endsAt')}</Th>
                <Th>{t('portalContent.challenges.columns.completions')}</Th>
                <Th>
                  <span className="sr-only">{t('common.actions')}</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {shown.map((challenge, index) => (
                <tr
                  key={challenge.id}
                  className={`border-b border-subtle/50 ${index % 2 === 1 ? 'bg-row-odd' : 'bg-row-even'}`}
                >
                  <Td>
                    <div className="grid h-10 w-10 place-items-center overflow-hidden rounded-control bg-input">
                      {challenge.imageUrl ? (
                        <img
                          src={challenge.imageUrl}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Trophy className="h-4 w-4 text-content-muted" aria-hidden />
                      )}
                    </div>
                  </Td>
                  <Td>
                    <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-primary">
                      {t(`portalContent.kinds.${challenge.kind}`)}
                    </span>
                  </Td>
                  <Td>
                    <p className="font-medium text-content">{challenge.title}</p>
                    {challenge.description ? (
                      <p className="max-w-64 truncate text-xs text-content-muted">
                        {challenge.description}
                      </p>
                    ) : null}
                  </Td>
                  <Td>
                    <span className={challenge.gameId ? 'text-content' : 'text-content-muted'}>
                      {gameLabel(challenge)}
                    </span>
                  </Td>
                  <Td>
                    {challenge.metric && challenge.targetValue !== null ? (
                      <span>
                        <span className="tabular">{challenge.targetValue}</span>{' '}
                        <span className="text-content-secondary">
                          {t(metricKey(challenge.metric))}
                        </span>
                      </span>
                    ) : (
                      <span className="text-content-muted">
                        {t('portalContent.challenges.oneShot')}
                      </span>
                    )}
                  </Td>
                  <Td>
                    <span className="tabular">{challenge.pointsReward}</span>{' '}
                    <span className="text-content-muted">{t('portalContent.pointsShort')}</span>
                  </Td>
                  <Td>
                    <input
                      type="checkbox"
                      checked={challenge.isActive}
                      disabled={togglingId === challenge.id}
                      aria-label={t('portalContent.challenges.toggleActive', {
                        title: challenge.title,
                      })}
                      onChange={(event) => void handleToggle(challenge, event.target.checked)}
                      className="h-4 w-4 accent-[var(--brand-primary)]"
                    />
                  </Td>
                  <Td>
                    <span className="tabular">{dateTime(challenge.startsAt)}</span>
                  </Td>
                  <Td>
                    <span className="tabular">{dateTime(challenge.endsAt)}</span>
                  </Td>
                  <Td>
                    <span className="tabular">{challenge.completionCount}</span>
                  </Td>
                  <Td>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing(challenge)}
                        title={t('common.edit')}
                        aria-label={t('common.edit')}
                        className="grid h-7 w-7 place-items-center rounded-control text-content-secondary hover:bg-hover hover:text-content"
                      >
                        <Pencil className="h-4 w-4" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(challenge)}
                        title={t('common.delete')}
                        aria-label={t('common.delete')}
                        className="grid h-7 w-7 place-items-center rounded-control text-danger hover:bg-hover"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {creating ? (
        <ChallengeDrawer
          challenge={null}
          onClose={() => setCreating(false)}
          onSaved={() => void reload()}
        />
      ) : null}

      {editing ? (
        <ChallengeDrawer
          challenge={editing}
          onClose={() => setEditing(null)}
          onSaved={() => void reload()}
        />
      ) : null}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="whitespace-nowrap px-3 py-2 text-start text-xs font-medium uppercase tracking-wide text-content-muted"
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="whitespace-nowrap px-3 py-2 text-content">{children}</td>;
}

/**
 * The list header's game filter, sized like the other list-header filters
 * (Products, Customers, Bills) rather than as `${inputClass} w-auto`: both set
 * width and Tailwind emits `.w-auto` before `.w-full`, so inputClass's w-full
 * won and the select took a header row of its own, pushing Add challenge onto
 * a third (review, 2026-09-23). max-w-full keeps a long game title from
 * widening it past a phone. Exported so the node-environment tests can render
 * it without the page's fetch.
 */
export function ChallengeGameFilterSelect({
  value,
  options,
  onChange,
}: {
  value: ChallengeGameFilter;
  options: ReturnType<typeof challengeGameFilters>;
  onChange: (filter: ChallengeGameFilter) => void;
}) {
  const { t } = useTranslation();
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value as ChallengeGameFilter)}
      aria-label={t('portalContent.challenges.gameFilter.label')}
      className="max-w-full rounded-control border border-subtle bg-input px-2.5 py-1.5 text-sm text-content outline-none focus:border-primary"
    >
      <option value="all">{t('portalContent.challenges.gameFilter.all')}</option>
      <option value="venue">{t('portalContent.challenges.gameFilter.venueWide')}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.title}
        </option>
      ))}
    </select>
  );
}

/**
 * Add/edit a challenge — every field of `createChallengeSchema`. Exported so
 * the node-environment tests can statically render the metric options.
 */
export function ChallengeDrawer({
  challenge,
  onClose,
  onSaved,
}: {
  challenge: ChallengeDto | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = challenge !== null;

  // `challengeFormFromDto` is also the baseline `toChallengeUpdatePayload`
  // diffs against, which is what guarantees an untouched field is never sent.
  const [form, setForm] = useState<ChallengeFormState>(() => challengeFormFromDto(challenge));
  const [imageUrl, setImageUrl] = useState<string | null>(challenge?.imageUrl ?? null);

  const games = useResource<ChallengeGameOption[]>(CHALLENGE_GAMES_PATH);
  const gameOptions = gamePickerOptions(games.data, challenge);

  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const set = <K extends keyof ChallengeFormState>(key: K, value: ChallengeFormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const errorFor = (field: string) => errors.find((issue) => issue.field === field)?.message;

  /**
   * A challenge must be completable. Without a target it never finishes;
   * without a metric nothing ever counts toward it. Either way the customer
   * gets a row in the Arcade that sits at 0% forever, so the drawer refuses
   * both before the wire — and the API refuses them again behind it.
   */
  const missingTarget = challengeMissingTarget(form);
  const missingMetric = challengeMissingMetric(form);
  const incomplete = missingTarget || missingMetric;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (incomplete) return;
    setErrors([]);
    setSubmitting(true);

    try {
      if (isEdit && challenge) {
        // Diffed against what the drawer loaded: fields the venue changed
        // underneath the open drawer are not clobbered by an unrelated save.
        const payload = toChallengeUpdatePayload(form, challenge);
        await api.patch(`${CHALLENGES_PATH}/${challenge.id}`, payload);
      } else {
        await api.post(CHALLENGES_PATH, toChallengePayload(form));
      }
      onSaved();
      onClose();
    } catch (caught) {
      setErrors(toFieldErrors(caught, t('portalContent.challenges.saveFailed')));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="flex h-full w-full max-w-xl flex-col border-s border-subtle bg-raised shadow-2xl"
      >
        <header className="border-b border-subtle p-5">
          <h2 className="text-lg font-semibold text-content">
            {isEdit ? t('portalContent.challenges.edit') : t('portalContent.challenges.addNew')}
          </h2>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-auto p-5">
          <ImageUpload
            label={t('portalContent.challenges.fields.image')}
            kind="CHALLENGE_IMAGE"
            currentUrl={imageUrl}
            onUploaded={(assetId, url) => {
              set('imageMediaAssetId', assetId);
              setImageUrl(url);
            }}
            onCleared={() => {
              set('imageMediaAssetId', null);
              setImageUrl(null);
            }}
          />

          <Field label={t('portalContent.challenges.fields.kind')} error={errorFor('kind')}>
            <select
              value={form.kind}
              onChange={(event) => set('kind', event.target.value as ChallengeFormState['kind'])}
              className={inputClass}
            >
              {CHALLENGE_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`portalContent.kinds.${kind}`)}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t('portalContent.challenges.fields.title')} error={errorFor('title')}>
            <input
              value={form.title}
              onChange={(event) => set('title', event.target.value)}
              required
              maxLength={120}
              className={inputClass}
            />
          </Field>

          <Field
            label={t('portalContent.challenges.fields.description')}
            error={errorFor('description')}
          >
            <textarea
              value={form.description}
              onChange={(event) => set('description', event.target.value)}
              rows={3}
              maxLength={500}
              className={inputClass}
            />
          </Field>

          {/*
            A label for the kiosk's per-game "Explore challenges" / "Explore
            quests", not a filter on progress: the metrics below are venue
            figures, and the hint says so rather than let staff believe an
            hours challenge set on one game counts only that game's hours.
          */}
          <Field
            label={t('portalContent.challenges.fields.game')}
            hint={t('portalContent.challenges.fields.gameHint')}
            error={errorFor('gameId') ?? games.error ?? undefined}
          >
            <select
              value={form.gameId}
              onChange={(event) => set('gameId', event.target.value)}
              className={inputClass}
            >
              <option value="">{t('portalContent.challenges.allGames')}</option>
              {gameOptions.map((game) => (
                <option key={game.id} value={game.id}>
                  {game.inactive
                    ? t('portalContent.challenges.fields.gameInactive', { title: game.title })
                    : game.title}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label={t('portalContent.challenges.fields.pointsReward')}
            error={errorFor('pointsReward')}
          >
            <input
              type="number"
              value={form.pointsReward}
              onChange={(event) => set('pointsReward', event.target.value)}
              required
              min={0}
              max={1_000_000}
              className={inputClass}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field
              label={t('portalContent.challenges.fields.metric')}
              error={
                missingMetric
                  ? t('portalContent.challenges.fields.metricRequired')
                  : errorFor('metric')
              }
            >
              <select
                value={form.metric}
                onChange={(event) =>
                  set('metric', event.target.value as ChallengeFormState['metric'])
                }
                className={inputClass}
              >
                <option value="" disabled>
                  {t('portalContent.challenges.fields.metricNone')}
                </option>
                {CHALLENGE_METRICS.map((metric) => (
                  <option key={metric} value={metric}>
                    {t(metricKey(metric))}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label={t('portalContent.challenges.fields.targetValue')}
              hint={t('portalContent.challenges.fields.targetHint')}
              error={
                missingTarget
                  ? t('portalContent.challenges.fields.targetRequired')
                  : errorFor('targetValue')
              }
            >
              <input
                type="number"
                value={form.targetValue}
                onChange={(event) => set('targetValue', event.target.value)}
                min={1}
                max={1_000_000}
                className={inputClass}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field
              label={t('portalContent.challenges.fields.startsAt')}
              error={errorFor('startsAt')}
            >
              <input
                type="datetime-local"
                value={form.startsAt}
                onChange={(event) => set('startsAt', event.target.value)}
                className={`${inputClass} tabular`}
              />
            </Field>

            <Field
              label={t('portalContent.challenges.fields.endsAt')}
              hint={t('portalContent.challenges.fields.datesHint')}
              error={errorFor('endsAt')}
            >
              <input
                type="datetime-local"
                value={form.endsAt}
                onChange={(event) => set('endsAt', event.target.value)}
                className={`${inputClass} tabular`}
              />
            </Field>
          </div>

          <Field
            label={t('portalContent.challenges.fields.sortOrder')}
            error={errorFor('sortOrder')}
          >
            <input
              type="number"
              value={form.sortOrder}
              onChange={(event) => set('sortOrder', event.target.value)}
              min={0}
              max={9_999}
              className={inputClass}
            />
          </Field>

          <Toggle
            label={t('portalContent.challenges.fields.isActive')}
            checked={form.isActive}
            onChange={(value) => set('isActive', value)}
          />
        </div>

        <footer className="border-t border-subtle p-5">
          {errorFor('(root)') ? (
            <p role="alert" className="mb-3 text-sm text-danger">
              {errorFor('(root)')}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className={ghostPillClass}>
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={submitting || incomplete} className={pillClass}>
              {t('common.save')}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}
